/**
 * SVG / Canvas Scene provider (spec section 7 - "Local: SVG/Canvas").
 *
 * Exposes the procedural scene engine as a provider so the studio can
 * offer "generate" without any external service. Nothing is rendered
 * until export time - a procedural "asset" is code, not a file.
 */

import { VideoProvider, PROVIDER_STATUS } from './base.js';
import { listProceduralScenes } from '../scenes/procedural/index.js';

export class SVGSceneProvider extends VideoProvider {
  constructor() {
    super({
      id: 'procedural-scenes',
      name: 'Procedural Scenes (Canvas/SVG)',
      type: 'local',
      description:
        'Scenes drawn live by the Canvas engine during export. No download, no API, no storage cost - the guaranteed fallback when every other provider is unavailable.',
      docsUrl: null,
      capabilities: {
        type: 'local',
        automated: true,
        requiresApiKey: false,
        requiresWorker: false,
        manualStepRequired: false,
        asynchronous: false,
        supportsCancel: true,
        nativeAudio: false,
        durations: [],
        aspectRatios: ['9:16', '1:1', '16:9'],
        resolutions: ['720x1280', '1080x1920'],
        costModel: 'none',
        costNote: 'Free and offline. Quality is stylised rather than photoreal.',
        browserAutomation: false,
      },
    });
  }

  async status() {
    const scenes = listProceduralScenes();
    return {
      state: PROVIDER_STATUS.READY,
      detail: `${scenes.length} procedural scenes available offline.`,
      checkedAt: new Date().toISOString(),
      meta: { scenes },
    };
  }

  async validateCredentials() {
    return { configured: true, detail: 'No credentials required.' };
  }

  async generate({ scene }) {
    return {
      mode: 'procedural',
      providerId: this.id,
      sceneId: scene?.id ?? null,
      detail:
        'Procedural scenes are drawn at export time, so there is no asset to download. Select the scene and render.',
      available: listProceduralScenes(),
    };
  }
}
